/**
 * MemberWelcomeState - Member onboarding flow for new organization members
 */
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  Building2,
  FileText,
  MessageSquare,
  Sparkles,
} from "lucide-react";

export default function MemberWelcomeState({ org, myRole, members, usage, stats, onComplete, onSnooze }) {
  const navigate = useNavigate();
  const agreements = stats?.agreements || [];
  const hasAgreements = agreements.length > 0;
  const memberCount = org.member_count || 0;

  const handleOpenAgreement = (agreementId) => {
    onComplete("open_agreement");
    navigate(`/ask?contract=${agreementId}`);
  };

  const handleRunAnalysis = () => {
    onComplete("run_prompt");
    navigate("/ask");
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="member-welcome-state">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-5">
          {/* Header */}
          <div className="mb-2">
            <h1
              className="font-heading text-2xl font-semibold mb-1"
              data-testid="member-welcome-heading"
            >
              Welcome to {org.name}
            </h1>
            <p className="text-muted-foreground text-sm">
              You've been added as a{" "}
              <span className="font-medium text-foreground capitalize">
                {myRole}
              </span>
              . Here's how to get started.
            </p>
          </div>

          {/* Section 1 — Agreements */}
          <div
            className="bg-card border border-border rounded-lg p-5"
            data-testid="member-welcome-agreements"
          >
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex-1">
                {hasAgreements ? (
                  <>
                    <h3 className="font-medium text-sm mb-1">
                      Shared Agreements
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3">
                      Start by reviewing your organization's shared agreement.
                    </p>
                    <div className="space-y-2">
                      {agreements.map((a) => (
                        <div
                          key={a.id}
                          className="flex items-center justify-between bg-muted/40 rounded-md px-3 py-2.5"
                          data-testid={`agreement-row-${a.id}`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm truncate">{a.name}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-7 px-2.5 flex-shrink-0"
                            onClick={() => handleOpenAgreement(a.id)}
                            data-testid={`open-agreement-${a.id}`}
                          >
                            Open
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-medium text-sm mb-1">
                      Organization Agreements
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Your organization hasn't uploaded an agreement yet. Reach
                      out to your organization admin to get started.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Section 2 — First analysis */}
          <div
            className={`bg-card border rounded-lg p-5 transition ${
              hasAgreements ? "border-primary/30 shadow-sm" : "border-border opacity-60"
            }`}
            data-testid="member-welcome-analysis"
          >
            {!hasAgreements && (
              <div className="absolute inset-0 rounded-lg" />
            )}
            <div className="flex items-start gap-4">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  hasAgreements
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm mb-1">
                  Run Your First Analysis
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Try a suggested prompt to see how the platform extracts
                  obligations, timelines, and procedures from your agreement.
                </p>
                <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-muted/50 rounded-md">
                  <Sparkles className="w-3 h-3 text-primary/60 flex-shrink-0" />
                  <span className="text-xs text-foreground/70">
                    "Summarize key obligations."
                  </span>
                </div>
                <Button
                  size="sm"
                  disabled={!hasAgreements}
                  onClick={handleRunAnalysis}
                  data-testid="member-run-analysis-btn"
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                  Run First Analysis
                </Button>
              </div>
            </div>
          </div>

          {/* Snooze (24h) */}
          <div className="text-right">
            <button
              onClick={onSnooze}
              className="text-xs text-muted-foreground hover:text-foreground transition underline"
              data-testid="dismiss-member-welcome"
            >
              Skip to dashboard
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4" data-testid="member-welcome-sidebar">
          <div className="bg-card border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2.5">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">{org.name}</span>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Your Role</span>
                <span className="font-medium capitalize">{myRole}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Members</span>
                <span className="font-medium">{memberCount} / 10</span>
              </div>
            </div>
          </div>

          {usage && (
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                Usage This Month
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MessageSquare className="w-3 h-3" />
                    Questions
                  </span>
                  <span className="font-medium text-xs" data-testid="member-sidebar-questions">
                    {usage.questions_used}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FileText className="w-3 h-3" />
                    Case Reviews
                  </span>
                  <span className="font-medium text-xs" data-testid="member-sidebar-reviews">
                    {usage.reviews_used}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
